import { Box, Button, Chip, Grid, Stack, Typography } from "@mui/material";
import { t } from "i18next";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { APP_ICONS } from "../../../assets";
import AppLoader from "../../../components/AppLoader";
import PaperContainer from "../../../components/Containers/PaperContainer";
import Label from "../../../components/Label";
import NavigationDrawer from "../../../components/NavigationDrawer";
import GlobalTable from "../../../components/Tables/GlobalTable";
import TextInput from "../../../components/TextInputs";
import {
  CURRENCY,
  ORDER_STATUS_TRANSITIONS,
} from "../../../constants/Variables";
import globalStyle from "../../../globalStyle";
import {
  getOrderDetailById,
  updateSupplierOrderItemStatus,
  updateSupplierOrderStatusInBulk,
} from "../../../services/orderApiFn";
import {
  calculateProductOrderVatAmount,
  calculateSupplierTotalOrderPrice,
  calculateVat,
  handleOpenSnackBar,
  roundTo2Decimal,
} from "../../../utils";
import { PDFDownloadLink } from "@react-pdf/renderer";
import EcommerceInvoicePDF from "../../../components/InvoiceTemplate/Ecommerce";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useTranslation } from "react-i18next";
import { getDonationByOrderId } from "../../../services/donationApiFn";
import GlobalButton from "../../../components/CustomButton";

const headerData = [
  "store",
  "name",
  "variant",
  "quantity",
  "price",
  "profitMargin",
  "discount",
  "finalPrice",
  "vat",
  "vatAmount",
  "amountAfterVat",
  "status",
];

const tableRows = [
  "product_store",
  "product_name",
  "order_product_variant",
  "order_product_quantity",
  "order_product_unit_price",
  "order_product_profit_margin",
  "order_product_discount",
  "order_product_final_price",
  "order_product_vat",
  "order_product_vat_amount",
  "order_product_amount_after_vat",
  "product_status",
];

const AdminOrderDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(["sales", "common"]);
  const [isLoading, setIsLoading] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [supplierOrders, setSupplierOrders] = useState([]);
  const [userInfo, setUserInfo] = useState(null);
  const [userAddress, setUserAddress] = useState(null);
  const [donationDetails, setDonationDetails] = useState(null);
  const [isBulkMode, setIsBulkMode] = useState(false);
  const [pendingStatusChanges, setPendingStatusChanges] = useState({});

  // Get the user data from the redux store
  const userData = useSelector((state) => state.userData.user);
  const currentLanguage = useSelector(
    (state) => state.languageReducer.currentLanguage,
  );

  const fetchOrderDetail = async () => {
    try {
      setIsLoading(true);
      const response = await getOrderDetailById(id);
      if (response?.data.status === "success") {
        let detail = response?.data?.data;
        let order = response?.data?.data?.order;
        let userInfo = response?.data?.data?.userId;
        let userAddress = response?.data?.data?.address;

        const modifiedOrder = order.map((item) => ({
          ...item,
          savedOrderStatus: item.orderStatus,
        }));

        setOrderDetails(detail);
        setUserInfo(userInfo);
        setUserAddress(userAddress);
        setSupplierOrders(modifiedOrder);
      } else {
        handleOpenSnackBar("error", response?.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDonationByOrderId = async (orderId) => {
    try {
      const response = await getDonationByOrderId(orderId);
      if (response?.data.status === "success") {
        setDonationDetails(response?.data?.data);
      } else {
        console.error(response?.data?.message);
      }
    } catch (error) {
      console.error(error?.message);
    }
  };

  useEffect(() => {
    if (id) {
      fetchOrderDetail();
      fetchDonationByOrderId(id);
    }
  }, [id]);

  const handleChangeOrderStatus = async (e, row) => {
    const savedStatus = row?.savedOrderStatus ?? row?.orderStatus;

    if (
      savedStatus !== e &&
      !ORDER_STATUS_TRANSITIONS[savedStatus]?.includes(e)
    ) {
      handleOpenSnackBar(
        "error",
        `Cannot change status from ${savedStatus} to ${e}`,
      );
      return;
    }

    if (isBulkMode) {
      setPendingStatusChanges((prev) => {
        const updated = { ...prev };

        if (e === savedStatus) {
          // Back to original status - no real change, drop it from pending list
          delete updated[row._id];
        } else {
          updated[row._id] = e;
        }

        return updated;
      });
      return;
    }

    try {
      setIsLoading(true);
      const response = await updateSupplierOrderItemStatus(
        orderDetails._id,
        row._id,
        { orderStatus: e },
      );
      if (response?.data.status === "success") {
        handleOpenSnackBar("success", response?.data.message);
        fetchOrderDetail();
      } else {
        handleOpenSnackBar("error", response?.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBulkUpdateSubmit = async () => {
    const updates = Object.entries(pendingStatusChanges).map(
      ([itemId, orderStatus]) => ({
        itemId,
        orderStatus,
      }),
    );

    if (updates.length === 0) {
      handleOpenSnackBar("info", "Please select at least one status change");
      return;
    }

    try {
      setIsLoading(true);
      const response = await updateSupplierOrderStatusInBulk(orderDetails._id, {
        updates,
      });
      if (response?.data.status === "success") {
        handleOpenSnackBar("success", response?.data.message);
        setPendingStatusChanges({});
        fetchOrderDetail();
      } else {
        handleOpenSnackBar("error", response?.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const totalVatAmount = useMemo(
    () => calculateProductOrderVatAmount(supplierOrders),
    [supplierOrders],
  );

  const discountVat = useMemo(
    () =>
      roundTo2Decimal(
        calculateVat(
          orderDetails?.discount?.amount || 0,
          orderDetails?.vat || 0,
        ),
      ),
    [orderDetails?.discount?.amount, orderDetails?.vat],
  );

  const supplierTotalOrderPrice = useMemo(
    () =>
      calculateSupplierTotalOrderPrice(supplierOrders) +
      orderDetails?.shippingFee +
      orderDetails?.platformFee +
      totalVatAmount,
    [supplierOrders, orderDetails],
  );

  const hasAnyProductInContinueStage = supplierOrders?.some(
    (item) =>
      item.orderStatus === "pending" ||
      item.orderStatus === "processing" ||
      item.orderStatus === "shipped",
  );

  const renderRightAction = () => {
    return (
      <Stack direction="row" justifyContent={"center"}>
        <PDFDownloadLink
          document={
            <EcommerceInvoicePDF
              supplierOrders={supplierOrders}
              invoiceData={orderDetails}
              currentLanguage={currentLanguage}
              donationDetails={donationDetails}
            />
          }
          fileName={`invoice-${orderDetails?.orderId}.pdf`}
        >
          {({ blob, url, loading, error }) => (
            <Button
              variant="contained"
              disabled={loading}
              sx={{
                width: "auto",
                height: "30px",
                borderRadius: "6px",
              }}
              startIcon={<FileDownloadIcon />}
            >
              {loading
                ? t("generating", { ns: "common" })
                : t("invoice", { ns: "common" })}
            </Button>
          )}
        </PDFDownloadLink>
      </Stack>
    );
  };

  return (
    <NavigationDrawer>
      <AppLoader isVisible={isLoading} />
      <PaperContainer
        title={t("saleDetail")}
        subtitle={t("viewAndManageSaleDetails")}
        headerActions={
          !hasAnyProductInContinueStage ? renderRightAction() : null
        }
        showBackButton={true}
        onBack={() => navigate(-1)}
      >
        <Grid container spacing={2}>
          {orderDetails?.isPackageOrder && (
            <Grid item xs={12}>
              <Chip
                icon={<img src={APP_ICONS.packageWhiteIcon} width={"20px"} />}
                label={t(
                  orderDetails?.packageDetail?.packageType === "custom"
                    ? "customPackageOrder"
                    : "basePackageOrder",
                )}
                color="primary"
              />
            </Grid>
          )}

          {orderDetails?.discount?.type === "slab" && (
            <Grid item xs={12}>
              <Chip
                icon={<img src={APP_ICONS.packageWhiteIcon} width={"20px"} />}
                label={t("planOrder")}
                color="primary"
              />
            </Grid>
          )}
          {orderDetails?.discount?.type === "promo" && (
            <Grid item xs={12}>
              <Chip
                icon={<img src={APP_ICONS.packageWhiteIcon} width={"20px"} />}
                label={t("promoApplied")}
                color="primary"
              />
            </Grid>
          )}

          {orderDetails?.event?.eventId && (
            <Grid item xs={12}>
              <Chip label={t("giftRegistryOrder")} color="secondary" />
            </Grid>
          )}

          <Grid item md={12}>
            <Typography
              variant="h3"
              fontSize={20}
              fontWeight={600}
              textTransform={"capitalize"}
            >
              {t("clientInformation")}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Label labelText={t("clientFullName")} required />
            <TextInput
              readonly
              placeholder="username"
              styleProps={globalStyle.textField}
              value={`${userInfo?.firstname || ""} ${userInfo?.lastname || ""}`.trim()}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Label labelText={t("email")} required />
            <TextInput
              readonly
              placeholder={t("userEmail")}
              styleProps={globalStyle.textField}
              value={userInfo?.email || ""}
            />
          </Grid>
          {userInfo?.contact && (
            <Grid item xs={12} md={4}>
              <Label labelText={t("contactNumber")} required />
              <TextInput
                readonly
                placeholder={t("contactNumber")}
                styleProps={globalStyle.textField}
                value={userInfo?.contact || ""}
              />
            </Grid>
          )}
        </Grid>

        <Grid container spacing={2} mt={2}>
          <Grid item md={12}>
            <Typography
              variant="h3"
              fontSize={20}
              fontWeight={600}
              textTransform={"capitalize"}
            >
              {t("shippingInformation")}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Label labelText={t("fullName")} required />
            <TextInput
              readonly
              placeholder={t("fullName")}
              styleProps={globalStyle.textField}
              value={`${userAddress?.firstname || ""} ${userAddress?.lastname || ""}`.trim()}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Label labelText={t("email")} required />
            <TextInput
              readonly
              placeholder={t("userEmail")}
              styleProps={globalStyle.textField}
              value={userAddress?.email || ""}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Label labelText={t("contactNumber")} required />
            <TextInput
              readonly
              placeholder={t("contactNumber")}
              styleProps={globalStyle.textField}
              value={userAddress?.contact || ""}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Label labelText={t("country")} required />
            <TextInput
              readonly
              placeholder={t("country")}
              styleProps={globalStyle.textField}
              value={userAddress?.country || ""}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Label labelText={t("state")} required />
            <TextInput
              readonly
              placeholder={t("state")}
              styleProps={globalStyle.textField}
              value={userAddress?.state || ""}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Label labelText={t("city")} required />
            <TextInput
              readonly
              placeholder={t("city")}
              styleProps={globalStyle.textField}
              value={userAddress?.city || ""}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Label labelText={t("zipCode")} required />
            <TextInput
              readonly
              placeholder={t("zipCode")}
              styleProps={globalStyle.textField}
              value={userAddress?.zipCode || ""}
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <Label labelText={t("addressDestination")} required />
            <TextInput
              readonly
              placeholder={t("addYourActivityDescription")}
              styleProps={globalStyle.textField}
              value={userAddress?.address || ""}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} mt={2}>
          <Grid item md={12}>
            <Typography
              variant="h3"
              fontSize={20}
              fontWeight={600}
              textTransform={"capitalize"}
            >
              {t("orderInformation")}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Label labelText={t("createdAt")} required />
            <TextInput
              readonly
              placeholder={t("createdAt")}
              styleProps={globalStyle.textField}
              value={`${moment(orderDetails?.createdAt).format("MM/DD/YYYY")}`}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Label labelText={t("orderId")} required />
            <TextInput
              readonly
              placeholder={t("orderId")}
              styleProps={globalStyle.textField}
              value={orderDetails?.orderId || ""}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Label labelText={t("deliveryType")} required />
            <TextInput
              readonly
              placeholder={t("deliveryType")}
              styleProps={globalStyle.textField}
              value={orderDetails?.deliveryType || 0}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Label labelText={t("shippingFee")} required />
            <TextInput
              readonly
              placeholder={t("shippingFee")}
              styleProps={globalStyle.textField}
              value={orderDetails?.shippingFee || 0}
              InputEndIcon={CURRENCY}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Label labelText={t("totalBill")} required />
            <TextInput
              readonly
              placeholder={t("totalBill")}
              styleProps={globalStyle.textField}
              value={
                roundTo2Decimal(
                  supplierTotalOrderPrice -
                    (orderDetails?.discount?.amount || 0) -
                    discountVat,
                ) || 0
              }
              InputEndIcon={CURRENCY}
            />
          </Grid>
        </Grid>

        {/* Gift registry event info — only shown when this order was placed
            against an event (i.e. a guest claimed a product from someone's
            gift registry). Same readonly Label/TextInput pattern as the
            sections above so it fits the rest of the page visually. */}
        {orderDetails?.event?.eventId && (
          <Grid container spacing={2} mt={2}>
            <Grid item md={12}>
              <Typography
                variant="h3"
                fontSize={20}
                fontWeight={600}
                textTransform={"capitalize"}
              >
                {t("eventInformation")}
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <Label labelText={t("eventName")} />
              <TextInput
                readonly
                styleProps={globalStyle.textField}
                value={orderDetails.event.eventId.title || ""}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Label labelText={t("eventDate")} />
              <TextInput
                readonly
                styleProps={globalStyle.textField}
                value={
                  orderDetails.event.eventId.eventDate
                    ? moment(orderDetails.event.eventId.eventDate).format(
                        "MM/DD/YYYY",
                      )
                    : ""
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Label labelText={t("claimDeadline")} />
              <TextInput
                readonly
                styleProps={globalStyle.textField}
                value={
                  orderDetails.event.eventId.claimDeadline
                    ? moment(orderDetails.event.eventId.claimDeadline).format(
                        "MM/DD/YYYY",
                      )
                    : ""
                }
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Label labelText={t("hostedBy")} />
              <TextInput
                readonly
                styleProps={globalStyle.textField}
                value={`${orderDetails.event.eventId.host?.firstname || ""} ${orderDetails.event.eventId.host?.lastname || ""}`.trim()}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Label labelText={t("hostEmail")} />
              <TextInput
                readonly
                styleProps={globalStyle.textField}
                value={orderDetails.event.eventId.host?.email || ""}
              />
            </Grid>

            <Grid item xs={12} md={12}>
              <Label labelText={t("invitationMessage")} />
              <TextInput
                readonly
                multiline
                rows={3}
                styleProps={globalStyle.textField}
                value={orderDetails.event.eventId.invitationMessage || ""}
              />
            </Grid>
          </Grid>
        )}

        {!hasAnyProductInContinueStage &&
          donationDetails &&
          Number(donationDetails?.actualAmount) > 0 && (
            <Box
              mt={3}
              p={2}
              sx={{
                bgcolor: "#f3e8ff",
                borderLeft: "4px solid",
                borderColor: "primary.main",
                borderRadius: 1,
              }}
            >
              <Typography
                variant="subtitle2"
                fontWeight={700}
                color="primary.main"
                textTransform="uppercase"
                letterSpacing={0.5}
                mb={0.5}
              >
                {t("charitableDonation", { ns: "common" })}
              </Typography>
              <Typography variant="body2" color="text.primary">
                {t("donationInvoiceNotice", {
                  ns: "common",
                  amount: roundTo2Decimal(donationDetails.actualAmount),
                  currency: CURRENCY,
                  percentage: donationDetails?.donationPercentage ?? 0,
                  organization:
                    donationDetails?.organization?.name?.[currentLanguage] ||
                    donationDetails?.organization?.name?.en ||
                    donationDetails?.organization?.name?.ro ||
                    "-",
                })}
              </Typography>
            </Box>
          )}

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mt={3}
          mb={2}
        >
          <Typography variant="h3" fontSize={20} fontWeight={600}>
            {t("allOrders")}
          </Typography>
          <Stack direction="row" gap={2} alignItems="center">
            <GlobalButton
              variant={"outlined"}
              btnText={
                isBulkMode ? "switchToSingleUpdate" : "switchToBulkUpdate"
              }
              handleClickBtn={() => {
                setIsBulkMode((prev) => !prev);
                setPendingStatusChanges({});
              }}
              styleProps={{
                width: "auto",
              }}
            />
            {isBulkMode && (
              <GlobalButton
                variant={"contained"}
                disabled={Object.keys(pendingStatusChanges).length === 0}
                btnText={`updateAll (${Object.keys(pendingStatusChanges).length})`}
                handleClickBtn={handleBulkUpdateSubmit}
                styleProps={{
                  width: "auto",
                }}
              />
            )}
          </Stack>
        </Stack>

        <GlobalTable
          minWidth={1800}
          tableHeader={headerData}
          displayRows={tableRows}
          tableData={supplierOrders}
          handleChangeOrderStatus={handleChangeOrderStatus}
          showStatus={true}
          isBulkMode={isBulkMode}
          pendingStatusChanges={pendingStatusChanges}
          isPackageOrder={orderDetails?.isPackageOrder || false}
          orderDetails={orderDetails}
          onRefresh={fetchOrderDetail}
          shippingFee={orderDetails?.shippingFee}
          deliveryType={orderDetails?.deliveryType}
        />
      </PaperContainer>
    </NavigationDrawer>
  );
};

export default AdminOrderDetails;
